import { ModalShell } from '../design/ModalShell';
import { WizardJourney } from '../design/WizardJourney';
import { FormField } from '../design/FormField';
import { IdentityCard } from '../design/IdentityCard';
import { PreviewCard } from '../design/PreviewCard';
import { Button } from '../design/Button';
import { SelectControl } from '../design/SelectControl';
import { TagsControl } from '../design/TagsControl';
import { identityFieldsFrom, integrationsFrom } from './vmAdapters';
import type { UploadOverlayVm } from '../useUploadOverlay';

/**
 * Tool Upload P2 — "Describe your tool" (wizard step 1)
 *
 * The first of the four numbered steps, and the frame that establishes the
 * two-column wizard body the later steps reuse.
 *
 * MEASURED from `Tool Upload P2.svg`. The modal's true box is 190,231 1056x640
 * r16 (collapsing the inside-stroke half-pixels of 190.5,231.5 1055x639 r15.5),
 * so it spans x 190..1246 and y 231..871 — identical on P1–P5.
 *
 * THREE RULES DIVIDE IT, and their spans are the structure:
 *
 *   header  191,328.5 -> 1245,328.5   full width
 *   column  485.5,329 -> 485.5,870    the full body height
 *   footer  486,788.5 -> 1245,788.5   RIGHT COLUMN ONLY, not full width
 *
 * The footer rule starting at x=486 rather than 190 is the load-bearing fact.
 * The vertical column rule already closes the left side, so the horizontal rule
 * begins at the column boundary and the left panel runs uninterrupted to the
 * modal's bottom edge. A border on a full-width footer element would draw a line
 * across the left panel that the design does not have.
 *
 * BANDS: header 231..328.5 (97.5), body 328.5..871, and within the right column
 * a footer band 788.5..871 (82.5).
 *
 * COLUMNS: left 190..485.5 = 295.5 wide (--wizard-rule-x), right 485.5..1246.
 *
 * INSETS, all landing in one 31.5-34 family that matches --modal-pad-x:
 *   left panel content   x=224     34 from the modal's left edge
 *   panel right -> rule  454..485.5   31.5
 *   form fields          x=518.5   33 from the rule
 *   form right edge      1212.5    33.5 from the modal's right edge
 *
 * THE STEP PILL ROW IS INSET 65, NOT 31.5. Pills run 255..406, 500..682,
 * 776..943, 1037..1181, so 65 + 151 + 94 + 182 + 94 + 167 + 94 + 144 + 65 = 1056
 * closes exactly against the modal width — four pills, three gaps, two insets,
 * no residue. Handled by ModalShell's headerInset="pills". The pill widths differ
 * because each hugs its own label, which is why the row is a fixed-gap flex row.
 *
 * VERTICAL RHYTHM IN THE FORM. Uniform, and it needed converting from baselines
 * to line boxes (see FormField's header and --leading-body):
 *   header rule -> first label box top  30     (--wizard-rule-gap-form)
 *   label box bottom -> field top        6     all five fields (--label-field-gap)
 *   field bottom -> next label box top  14     all transitions (--field-row-gap)
 * The row PITCH varies (71 / 111 / 71) only because the description field is 71
 * tall rather than 31. A flex column with one gap reproduces that for free; three
 * different pitches would be three magic numbers standing in for one rule.
 *
 * Both the 30 and the 14 were off by one until measured off the field RECTS
 * rather than the label baselines, and because they compound down the column the
 * two 1px errors rendered as +3.5/+4.5/+5.5/+6.5. Derived from the rects, the
 * five label box tops are 127.5 / 127.5 / 198.5 / 309.5 / 380.5 relative to the
 * modal's top, and the three field-to-label transitions are 14 exactly.
 *
 * FIELD BOXES, all r7.5, stroke #022B30 @ 0.2, no fill:
 *   Tool Name*          518.5, 384.5   343 x 31
 *   Primary Category*   894.5, 384.5   318 x 31
 *   Tool Description*   518.5, 455.5   694 x 71
 *   Tags*               518.5, 566.5   694 x 31
 *   Time Saved*         518.5, 637.5   343 x 31
 * The first row is two columns (343 + 33 + 318 = 694, closing exactly on the
 * full-width fields below it), so the row is grid-cols-form — a 343:318 ratio,
 * not equal halves.
 *
 * FOOTER, matching P1's measured values exactly:
 *   Cancel  1094.5,815.5  59x22  r7.5  stroke #022B30 @ 0.3
 *   Next    1168.5,815.5  48x22  r7.5  stroke #022B30 @ 0.3
 *     -> gap 15 (--modal-footer), right inset 29 (--modal-footer-r).
 *     The 22 is a NOMINAL stroked height; the true box is 815..838 = 23, matching
 *     Button's `xs`. Same half-pixel collapse as the modal's own 1055 -> 1056.
 *   help line  518,833.5  "Are the details in your identity wrong? Get help
 *              here." — "here" is --accent-link at full strength (x=761.912) and
 *              the full stop is a separate run (x=784.847), so only the word is
 *              accented.
 *   The buttons and the help line are NOT vertically centred on each other:
 *   button centre 826.5, help box centre 829.5. Each takes its own offset from
 *   the footer rule (27 and 31), and the band closes with a 33.5 bottom pad.
 *
 * THE FOOTER LIVES INSIDE THE RIGHT COLUMN, not in ModalShell's footer slot.
 * This is load-bearing and was got wrong once. P2's footer rule is drawn
 * RIGHT-COLUMN-ONLY (x=486..1245): the source runs the LEFT panel uninterrupted
 * to the modal floor (PreviewCard bottom at modal-local 594, well below the
 * footer rule at 557.5), because the vertical column rule already closes the
 * left side. Both panels are STATIONARY — neither scrolls; they share the one
 * full-height interior region (modal-local 97.5..640).
 *
 * A full-width ModalShell footer band stacked below the body stole 82.5px from
 * that shared region and clipped the stationary left panel ~40px short, forcing
 * a scrollbar the design does not have. So the whole two-column region is
 * ModalShell `children` filling the modal height, and the footer is the right
 * column's own bottom group (pushed down with mt-auto — flow, not a coordinate).
 *
 * WHAT IS DELIBERATELY NOT HERE:
 *   - No modal title. P2's header band contains the step pills and nothing else;
 *     the only other text at that height in the source is off-canvas sidebar
 *     furniture at x=210. The modal is named via ModalShell's `label` rather than
 *     by a heading this page invents.
 *   - No field values, placeholders or filled state. All five boxes are empty in
 *     the source and no frame draws a focus, error or filled treatment.
 *   - No validation. Every label ends in `*` but no frame draws a required-field
 *     error, so `required` is passed for semantics only. See SOLVE_LATER A5.
 *
 * WHAT CHANGED IN THE BACKEND COPY. The layout, every className and the whole
 * derivation above are untouched. Only data and handlers were added:
 *
 *   1. `identityFields` / `toolPreview` fixture reads -> the live Vm (see vmAdapters).
 *   2. `navigate('/upload/N-…')` -> `vm.goToStage` / `vm.onNextFromDetails`. Note that
 *      Next is a GATE, not a navigation: it validates and, on failure, focuses the
 *      first invalid field and stays put. That is why the fields below now carry
 *      `invalid` and `registerRef` — the Design draws no error treatment (its own
 *      note says so, SOLVE_LATER A5), but the backend's validation needs somewhere
 *      to land, and FormField already had those props as pure additions.
 *   3. FIVE MORE FIELDS. The Design draws five; `DraftDetails` carries ten, and the
 *      server requires the rest. The added five use the same FormField, the same
 *      `gap-row-gap` rhythm and the same `grid-cols-form` two-up pairing as the
 *      drawn ones, so they extend the frame's own system rather than introducing a
 *      second one. The user chose this over dropping them from the form.
 *   4. THREE OF THE TEN ARE FOREIGN KEYS. `categoryId`, `accessId` and
 *      `departmentId` are numeric ids into `refData`; the Design draws Primary
 *      Category as a plain text box, which cannot represent one. They render a
 *      `<select>` through FormField's existing `children` slot — the slot the port
 *      added for exactly this ("a custom control like a <select>", FormField's own
 *      note) — carrying the drawn field's own box classes so the control keeps the
 *      measured 31px height and border it has in the frame.
 *   5. "Back" goes to P1, as the Design draws it — `onBack`, supplied by the frame.
 *      P1 IS behind us: the automation picker is the first frame in this same box (see
 *      UploadOverlayPages), so Back returns to it rather than dismissing the overlay.
 *      Leaving keeps the draft either way (see draftStore), so nothing is lost by
 *      stepping back and picking a different automation.
 *
 * STILL TODO(dev), unchanged from the Design: the "here" link has no target. The
 * design draws it as a link but specifies none, so it stays inert rather than being
 * pointed somewhere invented.
 */
export function UploadDescribePage({
  vm,
  onBack,
}: {
  vm: UploadOverlayVm;
  /** Return to P1, the automation picker — the frame before this one. */
  onBack: () => void;
}) {
  const { details, detailErrors, triedStage, onDetailsChange, registerDetailField } = vm;
  const { refData } = vm.props;

  // Errors are shown only once the step has been submitted, matching the backend's
  // established behaviour: `triedStage` is what turns validation from "typing" into
  // "you tried to advance". The Design draws no error state at all, so this is the
  // backend's own gate expressed through FormField's `invalid` prop.
  const showErrors = triedStage.details;
  const err = (field: keyof typeof detailErrors) => showErrors && Boolean(detailErrors[field]);

  return (
    <ModalShell
      label="Describe your tool"
      headerInset="pills"
      // The two-column steps own their scroller: the left rail and the footer are
      // STATIONARY by design and only the form column scrolls, so the body must not
      // be the scroller or the rail and footer would ride up with the fields. The
      // Design's P2 never needed this because it draws a fixed five fields that
      // always fit the 640 box; ten do not. See ModalShell's note at `bodyScroll`.
      bodyScroll={false}
      header={
        // The pills, then the full-width header rule beneath them. The header
        // region is inset by --step-row-inset (65), so the rule is -mx'd back
        // out by that same amount to span 191..1245 as the source draws it
        // while the pills stay within the inset. The 33 below the pills is the
        // same --space-11 as the 33 above them.
        <>
          <WizardJourney activeStep={1} />
          <div className="-mx-step-row mt-11 h-px bg-ink" />
        </>
      }
    >
      {/* The body: stationary left panel | 1px column rule | stationary right
          column. The middle grid track IS the rule, so the split stays in flow
          and no line is placed by coordinate.

          h-full — the grid fills the modal's whole interior below the header.
          Neither column scrolls (the top bar and both panels are fixed by
          design); the two columns share this one full-height region. The left
          panel runs its natural height with slack to the floor; the right
          column pins its footer to the bottom with mt-auto. This is why the
          footer is NOT a ModalShell footer slot — a full-width band there would
          shorten the left panel and reintroduce the clip. See the header note.

          NO pt- ON THE GRID. The two columns start at different heights — the
          form's first label box is 30 below the header rule, the panel's first
          heading box is 41.5 — so each column carries its own top gap and the
          rule track spans both. A shared pt- here would have to pick one and be
          11.5px wrong on the other.

          ONE ADDITION TO THE DESIGN'S CLASS LIST: `grid-rows-[minmax(0,1fr)]`
          plus `min-h-0`. `h-full` alone gives this grid a 538px BOX but leaves its
          single implicit row sized to CONTENT — measured at 826.5px against the
          538px box, i.e. the ten fields overflowing the modal floor by 288.5px,
          which pushed the mt-auto footer out of the box entirely and drew the left
          rail's cards past the bottom edge. An implicit row is `auto`, and `auto`
          means fit-content, so the box height never reached the columns. Naming the
          row `minmax(0,1fr)` makes it FILL the 538 instead of fitting the content,
          and `min-h-0` lets the columns shrink below their content so the right
          column's own `overflow-y-auto` finally has bounded space to scroll in.
          The Design's P2 never needed either: five fields fit 538, so its content
          row and its box happened to be the same height. */}
      <div className="grid h-full min-h-0 grid-rows-[minmax(0,1fr)] grid-cols-wizard-body">
        {/* LEFT PANEL. One flex column with the measured two-value rhythm:
            --wizard-heading-gap sits a heading above its card,
            --wizard-group-gap sits a card above the next heading. Those two
            plus the heading's own 20px line box sum to the verified 70px
            card-to-card distance. */}
        <div className="flex min-w-0 flex-col pl-wizard-left-rest pr-wizard-left-gap pt-wizard-rule-gap-panel">
          <h2 className="text-sm font-light text-ink">Your Identity</h2>
          <IdentityCard className="mt-wizard-heading" fields={identityFieldsFrom(vm.props)} />

          <h2 className="mt-wizard-group text-sm font-light text-ink">Tool Preview</h2>
          {/* The preview reflects what the builder has typed SO FAR: the name is the
              live `details.name`, falling back to the automation's own name (the
              thing being shared) rather than to invented copy, and the owner is the
              signed-in user. The integrations are the flow's real connectors, which
              are already in memory — see integrationsFrom. */}
          <PreviewCard
            className="mt-wizard-heading"
            name={details.name || vm.props.flow.name}
            owner={vm.props.builderName}
            integrations={integrationsFrom(vm.components)}
            // The plate holds a generated DIAGRAM of the automation, drawn from the
            // same in-memory manifest the integrations come from. `integrations`
            // no longer paints logo stubs — the art consumes it as a connector
            // count for the case where the structure is not readable.
            shape={vm.components}
          />
        </div>

        {/* The column rule: the grid's 1px middle track, full body height. */}
        <div className="bg-ink" aria-hidden="true" />

        {/* RIGHT COLUMN. A full-height flex column: the five fields at the top,
            then the footer group pushed to the modal floor with mt-auto. The
            column's own pl-wizard-right (33) sits its content at the form's left
            edge (x=518); the footer rule breaks back out of that padding to the
            column/rule boundary (x=486) where the source draws it. */}
        <div className="flex min-w-0 flex-col pl-wizard-right">
          {/* THE SCROLLER, and the only addition to this column's structure. The
              Design's five fields always fit, so its column needed none; ten do not
              fit the 640 box, and the alternative to scrolling here is scrolling the
              whole body — which would carry the stationary left rail and the footer
              up with it and break the frame the derivation above describes.
              `min-h-0 flex-1` is what makes the overflow resolve inside the flex
              column (see ModalShell's note).

              THE RIGHT EDGE: no horizontal margin or padding of its own. The
              fields keep ModalShell's px-modal-x, and that is correct — measured on
              the Design's own P2 at 1440, the right column's two gutters MATCH:
              column rule 490 -> fields 523 is 33 (pl-wizard-right), and fields 1215
              -> modal inner edge 1247.5 is 32.5 (--modal-pad-x). The footer rule is
              the ONLY thing that escapes to the modal edge (1246.5), because it is
              an underline rather than content.

              Two wrong turns are recorded here so neither is repeated. `pr-modal-x`
              DOUBLED the inset, insetting the fields ~64px and drawing a wide white
              band. `-mr-modal-x` then over-corrected the other way: it matched the
              fields to the footer rule's edge, which removed the right gutter
              entirely and left the form flush against the modal wall. The rule's
              edge was never the target — the LEFT gutter is.

              THE 30 IS THE SCROLLER'S PADDING, NOT THE PARENT'S GAP. This is the
              white-bar fix, and the distinction is the whole bug. The 30
              (--wizard-rule-gap-form) used to sit as `pt-` on the column ABOVE this
              scroller, which is correct in the Design because the Design's fields do
              not scroll: five fixed fields always fit the 640 box, so the 30 is just
              visible gap between the header rule and the first label, and you can see
              it sitting there at rest.

              Once the column scrolls, an OUTSIDE gap becomes a dead strip. The
              scroller's top edge is then 30 below the rule, so content is clipped at
              that line and the 30 above it is permanently blank surface — which is
              exactly the "white bar that sits above the text" (the user: "I only want
              it to sit to the right and to the left as it currently does. Not when I
              scroll. I want it to move with the text as it goes"). The gap belonged to
              the container when it needed to belong to the content.

              Moving it INSIDE as `pt-` on the scroller reproduces the Design exactly at
              rest — first label still 30 below the rule — and when scrolling, the text
              travels up THROUGH that 30 and is clipped at the rule itself. No strip,
              because there is no longer any space outside the scroll area.

              THE BOTTOM IS THE SAME 30, and it is measured, not mirrored for symmetry's
              sake. `pb-row-gap` (14, --field-row-gap) was the field-to-field rhythm
              borrowed as an edge inset by the port that added this scroller; it is not a
              measured distance to a rule. The frame's own rule-to-content gap below is
              --wizard-footer-help-gap (30) — footer rule to the help line — the same 30
              as above. So 30/30 is what the source draws at both rules, which is also
              what the user asked for ("some on the bottom providing it with position the
              same distance as the top text is from the top"). */}
          {/* THE SCROLLBAR SITS AT THE MODAL WALL, not in the gutter. `-mr-modal-x`
              takes the scroller's right EDGE out through ModalShell's px-modal-x to
              the modal's inner wall, so the scrollbar is drawn there rather than
              floating 31.5px inboard of it. `pr-modal-x` then gives that same 31.5
              back as CONTENT padding, so the fields do not move — only the track
              does.

              THIS IS NOT THE `-mr-modal-x` WRONG TURN recorded above. That attempt
              used the negative margin ALONE, which moved the fields out with the edge
              and left the form flush against the wall. Pairing it with `pr-modal-x`
              is what separates the two: the box grows right, the content does not.
              The measured 32.5px right gutter is preserved exactly. */}
          <div className="-mr-modal-x flex min-h-0 min-w-0 flex-1 flex-col gap-row-gap overflow-y-auto pb-wizard-rule-gap-form pr-modal-x pt-wizard-rule-gap-form">
            {/* Two-column row: 343:318, not 1fr 1fr. See grid-cols-form.
                RESPONSIVE — the row is single-column BELOW account (760) and
                the 343:318 two-up only AT >=760. The breakpoint is derived,
                not chosen: at exactly 760 the two-up fields measure 156/145,
                and 156 is PopupSmall's own single-stack field width (its narrow
                frame draws each field x=328.5 w=156). Below 760 the two-up
                fields fall under 100px and stop being usable, so 760 is the
                crossover where PopupSmall's single-stack takes over. Stacked,
                the two fields need the same 14px field rhythm between them, so
                gap-y-row-gap; the 33px column gap only applies once two-up. */}
            <div className="grid grid-cols-1 gap-y-row-gap account:grid-cols-form account:gap-x-col-gap account:gap-y-0">
              <FormField
                label="Tool Name*"
                name="tool-name"
                required
                value={details.name}
                onChange={(v) => onDetailsChange({ name: v })}
                invalid={err('name')}
                registerRef={(el) => registerDetailField('name', el)}
              />
              {/* An FK, so a select in the drawn box — see SelectControl and note 4. */}
              <FormField
                label="Primary Category*"
                name="primary-category"
                required
                // Reddens the label; the SelectControl below reddens the box.
                invalid={err('categoryId')}
              >
                <SelectControl
                  name="primary-category"
                  required
                  value={details.categoryId}
                  onChange={(id) => onDetailsChange({ categoryId: id })}
                  options={refData.categories}
                  invalid={err('categoryId')}
                  registerRef={(el) => registerDetailField('categoryId', el)}
                />
              </FormField>
            </div>

            <FormField
              label="Tool Description*"
              name="tool-description"
              size="tall"
              required
              value={details.description}
              onChange={(v) => onDetailsChange({ description: v })}
              invalid={err('description')}
              registerRef={(el) => registerDetailField('description', el)}
            />
            {/* `tags` is a string[] with a server-enforced cap, so the drawn box
                holds a chip input rather than a comma string — see TagsControl for
                why a plain text box cannot express the cap. */}
            <FormField label="Tags*" name="tags">
              <TagsControl
                details={details}
                onChange={onDetailsChange}
                suggestions={refData.tags}
              />
            </FormField>

            {/* Time Saved* is 343 wide — the left track — with the right half
                empty in the source. Reproduced as the same two-track grid with
                nothing in the second cell, so the field keeps its measured
                proportion instead of being handed a standalone width. Below
                account it is single-column like the row above, so the field
                runs the full narrowed column (PopupSmall draws Time Saved at
                the same 156 width as every other narrow field). No gap-y here:
                it holds one field. */}
            <div className="grid grid-cols-1 account:grid-cols-form account:gap-x-col-gap">
              <FormField
                label="Time Saved*"
                name="time-saved"
                required
                value={details.estimatedTimeSaved}
                onChange={(v) => onDetailsChange({ estimatedTimeSaved: v })}
                invalid={err('estimatedTimeSaved')}
                registerRef={(el) => registerDetailField('estimatedTimeSaved', el)}
              />
            </div>

            {/* ── The five the frame does not draw (note 3) ─────────────────────
                Same FormField, same `gap-row-gap` rhythm from the parent column,
                same `grid-cols-form` pairing for the two-up row. Ordered to match
                the frame's own logic: the long-form question first, then the two
                bounded choices side by side, then the two optional free-text
                fields last. */}
            <FormField
              label="Problem Solved*"
              name="problem-solved"
              size="tall"
              required
              value={details.problemSolved}
              onChange={(v) => onDetailsChange({ problemSolved: v })}
              invalid={err('problemSolved')}
              registerRef={(el) => registerDetailField('problemSolved', el)}
            />

            <div className="grid grid-cols-1 gap-y-row-gap account:grid-cols-form account:gap-x-col-gap account:gap-y-0">
              <FormField
                label="Access*"
                name="access"
                required
                // Reddens the label; the SelectControl below reddens the box.
                invalid={err('accessId')}
              >
                <SelectControl
                  name="access"
                  required
                  value={details.accessId}
                  onChange={(id) => onDetailsChange({ accessId: id })}
                  options={refData.accessRequirements}
                  invalid={err('accessId')}
                  registerRef={(el) => registerDetailField('accessId', el)}
                />
              </FormField>
              <FormField
                label="Department*"
                name="department"
                required
                // Reddens the label; the SelectControl below reddens the box.
                invalid={err('departmentId')}
              >
                <SelectControl
                  name="department"
                  required
                  value={details.departmentId}
                  onChange={(id) => onDetailsChange({ departmentId: id })}
                  options={refData.departments}
                  invalid={err('departmentId')}
                  registerRef={(el) => registerDetailField('departmentId', el)}
                />
              </FormField>
            </div>

            <FormField
              label="Dependencies"
              name="dependencies"
              size="tall"
              value={details.dependencies}
              onChange={(v) => onDetailsChange({ dependencies: v })}
              invalid={err('dependencies')}
              registerRef={(el) => registerDetailField('dependencies', el)}
            />

            {/* Requirements' label carries the asterisk only when the connector
                pre-detect found connectors — `vm.requirementsRequired` is the
                existing Stage 3.5b signal, and the server's 422 remains the real
                gate (see Share.tsx's inspectConnectors). The asterisk is therefore
                a real state of this field, not decoration. */}
            <FormField
              label={vm.requirementsRequired ? 'Requirements*' : 'Requirements'}
              name="requirements"
              size="tall"
              required={vm.requirementsRequired}
              value={details.requirements}
              onChange={(v) => onDetailsChange({ requirements: v })}
              invalid={err('requirements')}
              registerRef={(el) => registerDetailField('requirements', el)}
            />
          </div>

          {/* FOOTER GROUP — pinned to the modal floor by flow, not coordinate.
              mt-auto consumes the slack between the last field and the bottom,
              which is exactly the design's variable gap. The band itself is
              fixed: rule, then help + buttons, then --wizard-footer-pad-b.

              The rule is RIGHT-COLUMN-ONLY (x=486..1245). The right column
              already begins at the column/rule boundary and pads content in by
              --wizard-right (33) to the form edge, so the rule uses
              -ml-wizard-right to reach back to the boundary and pr-0 to run to
              the modal's right edge, while help + buttons keep the form edge.
              The -ml-px aligns the rule's own left edge onto the 1px column
              rule track. */}
          <div className="mt-auto pb-wizard-footer-b">
            {/* The rule runs the design's x=486..1245. -ml-wizard-right reaches
                back from the form edge to the column/rule boundary (486); the
                right end must escape ModalShell's body padding to reach the
                modal's inner edge, so -mr-modal-x cancels that --modal-pad-x
                (31.5) — a -wizard-right there would stop 1.5px short and a plain
                0 would stop the whole 31.5px short. Verified: L 297.5 / R -0.5
                from the modal edge against the design's 296 / 1. */}
            <div className="-ml-wizard-right -mr-modal-x h-px bg-ink" />
            {/* NOT items-center. The help line's box centre is 829.5 and the
                buttons' is 826.5 — 3px apart in the source, on every upload
                frame — so centring them against each other would put both 1.5px
                out. `items-start` and two measured top offsets instead.

                -mr-modal-x on the ROW, not the buttons: the row is
                justify-between, so extending its right boundary past the body's
                --modal-pad-x pushes only the buttons to the modal's true right
                inset (Next lands 29.5 from the modal edge, the design's value),
                while the help line's left edge is untouched. pr-modal-footer-r
                is then the 29 from that true edge. */}
            <div className="-mr-modal-x flex flex-wrap items-start justify-between gap-x-modal-footer gap-y-5 pr-modal-footer-r">
              <p className="min-w-0 pt-wizard-footer-help text-xs font-light text-ink">
                Are the details in your identity wrong? Get help{' '}
                {/* TODO(dev): opens identity/profile help. The design draws it as a link but
                    specifies no target — so it is a button styled as a link (it will trigger an
                    action, not navigate), which is also the accessible form (a bare href="#" is
                    not a valid destination). Wire the handler when the help target exists. */}
                <button
                  type="button"
                  className="text-accent-link underline-offset-2 hover:underline"
                >
                  here
                </button>
                .
              </p>
              {/* items-start, so pt-wizard-footer-btn puts the button's TOP edge
                  26.5 below the rule as measured. items-center here would centre
                  the button inside the taller flex line and lose the offset. */}
              <div className="flex shrink-0 items-start gap-modal-footer pt-wizard-footer-btn">
                {/* Back to P1, the automation picker — the previous FRAME, exactly as
                    the Design draws it (note 5). The draft is kept either way, so
                    stepping back to pick a different automation loses nothing. */}
                <Button variant="outline-square" size="xs" onClick={onBack}>
                  Back
                </Button>
                {/* A GATE, not a navigation: `onNextFromDetails` validates and, on
                    failure, focuses the first invalid field and stays on this step.
                    That is the backend's own behaviour, unchanged. */}
                <Button variant="outline-square" size="xs" onClick={vm.onNextFromDetails}>
                  Next
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}
